<script lang="ts">
	//Hooks
	import { createEventDispatcher } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import { Modal, Button, Text, Alert, Loader, ListItem, Stack } from '@Shelly/shelly-design-system';
	import type { WiFiScanRequest } from 'src/types/requests/settings/networks/wifi_request';

	export let wifiScanResult: WiFiScanRequest[];
	export let isOffline = false;

	const defaultMaxShowed = 9;
	let maxShowedNetworks = defaultMaxShowed;
	let showMore = false;
	let showModal = false;
	let waitForResponse = false;
	let networksListError = '';
	let availableNetworks = null;

	const dispatch = createEventDispatcher();
	const getAvailableNetworks = async () => {
		waitForResponse = true;
		dispatch('click');
	};

	const resetShowMore = () => {
		showMore = false;
		maxShowedNetworks = defaultMaxShowed;
	};

	const onSelect = (networkItem) => {
		showModal = false;
		resetShowMore();
		dispatch('select', networkItem);
	};

	const onResultData = (wifiScanResult: WiFiScanRequest[]) => {
		waitForResponse = false;
		const networksList = [];
		const checkDuplicateList = [];
		if (wifiScanResult?.length) {
			wifiScanResult.forEach((network) => {
				if (checkDuplicateList.indexOf(network.ssid) == -1) {
					const icon = `fa-wifi${network.rssi < -67 ? (network.rssi < -80 ? '-weak' : '-fair') : ''}`;
					networksList.push({
						value: network.ssid,
						title: network.ssid + (network.auth == 0 ? ' (open)' : ' '),
						is_open: network.auth == 0,
						icon,
					});
				}
				checkDuplicateList.push(network.ssid);
			});
		}
		if (!networksList?.length) {
			networksListError = $format('available_networks_error', {
				default: 'Could not load available networks.',
			});
		}
		availableNetworks = networksList;
	};

	$: onResultData(wifiScanResult);
</script>

<Button
	disabled={isOffline}
	size="m"
	width="full"
	variant="secondary"
	class="mb-s"
	on:click={() => {
		showModal = true;
		getAvailableNetworks();
	}}
	loading={waitForResponse}
>
	{$format('scan_for_wifi_networks', {
		default: 'Scan for Wi-Fi networks',
	})}
</Button>

<div class="modal">
	{#if showModal}
		<Modal
			on:close={() => {
				showModal = false;
				resetShowMore();
			}}
			{showModal}
			backdropClose={true}
			title={$format('available_networks', { default: 'Available networks' })}
		>
			<div slot="content">
				{#if waitForResponse}
					<div class="d-flex justify-content-center align-items-center flex-direction-column">
						<Loader class="mb-xs" />
						<Text styleType="controls-small" class="text-center text-bold">
							{$format('title_scanning', {
								default: 'Scanning ...',
							})}
						</Text>
					</div>
				{:else if availableNetworks?.length}
					<Stack gap="s">
						{#each availableNetworks as networkItem, i}
							{#if i < maxShowedNetworks}
								<ListItem
									viewType="fullColored"
									listItemBgColor="--color-1"
									icon={'fa-duotone ' + networkItem.icon}
									actionBtn
									title={networkItem.title}
									on:click={() => onSelect(networkItem)}
								/>
							{/if}
						{/each}
						{#if !showMore}
							<ListItem
								title={$format('show_more_network_items', {
									values: {
										networkItems: availableNetworks.length - maxShowedNetworks,
									},
									default: `+${availableNetworks.length - maxShowedNetworks} ${$format(
										'more',
										{
											default: 'more',
										},
									)}`,
								})}
								viewType="more"
								listItemBgColor="--color-1"
								on:click={() => {
									showMore = true;
									maxShowedNetworks = availableNetworks.length;
								}}
							/>
						{/if}
					</Stack>
				{:else}
					<Alert type="danger" heading={networksListError} class="mb-s" />
					<Button
						size="m"
						class="width-full mr-s text-capitalized"
						responsiveSize={{
							sm: 'm',
						}}
						variant="primary"
						on:click={getAvailableNetworks}
					>
						{$format('button_rescan', { default: 'Rescan' })}
					</Button>
				{/if}
			</div>
		</Modal>
	{/if}
</div>
